/**
 * Minimal live Firestore subscription hooks (suite-io had none).
 *
 * onSnapshot-based so handheld subjects/projects appear the moment a callable
 * writes them — no manual refresh. Pass a falsy path to no-op (e.g. before a
 * job is selected). Returns { data, loading, error }.
 */
import { useEffect, useState } from "react";
import { db, collection, doc, onSnapshot, query } from "../firebase.js";

export function useFirestoreCollection(path) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(Boolean(path));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) {
      setData([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, path)),
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useFirestoreCollection]", path, err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, [path]);

  return { data, loading, error };
}

export function useFirestoreDoc(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, path),
      (snap) => {
        setData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (err) => {
        console.error("[useFirestoreDoc]", path, err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, [path]);

  return { data, loading, error };
}
